// Google Drive connect/auth orchestration (TASK-VAULT-CLOUD-001).
//
// Web connect uses a direct Google OAuth PKCE flow (NOT Supabase signInWithOAuth)
// so the user's Supabase email/password session is never overwritten and we avoid
// the "PKCE verifier not found" error that occurs when Supabase tries to manage
// verifier state across a full-page navigation in Next.js.
//
// Flow (web):
//   startDriveWebConnect  → store PKCE verifier in cookie → redirect to Google
//   callback page (state=drive1) → read verifier → POST code+verifier to broker
//   broker exchanges with Google → stores refresh token → mints access token
//
// Flow (native):
//   capgo Google Sign-In → serverAuthCode → broker exchanges → stores refresh token
//
// Drive access tokens are short-lived; getDriveAccessToken() caches the current
// one in memory and re-mints via the broker just before expiry.

import { createClient } from '@/lib/supabase/client';
import { isNativeRuntime } from '@/lib/native/platform';
import { vaultApiPath } from '@/lib/vault/api';
import { DRIVE_APPDATA_SCOPE, nativeGoogleSignIn } from './socialLogin';

// ---------------------------------------------------------------------------
// PKCE helpers (browser SubtleCrypto — no external dependency)
// ---------------------------------------------------------------------------

function generateVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256b64url(plain: string): Promise<string> {
  const data = new TextEncoder().encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function setCookie(name: string, value: string, maxAge: number) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const match = document.cookie.split(';').find(c => c.trim().startsWith(name + '='));
  return match ? decodeURIComponent(match.trim().slice(name.length + 1)) : null;
}

/** Read PKCE state that was stored before the OAuth redirect. */
export function getDrivePkceFromCookies(): {
  verifier: string | null;
  redirectUri: string | null;
} {
  return {
    verifier: getCookie('drive_cv'),
    redirectUri: getCookie('drive_ru'),
  };
}

/** Erase PKCE cookies after use. */
export function clearDrivePkceCookies() {
  setCookie('drive_cv', '', 0);
  setCookie('drive_ru', '', 0);
}

// ---------------------------------------------------------------------------
// Broker fetch
// ---------------------------------------------------------------------------

async function supabaseAccessToken(): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function brokerFetch(suffix: string, init?: RequestInit): Promise<Response> {
  const token = await supabaseAccessToken();
  if (!token) throw new Error('Not signed in — please sign in to Seamigo first, then connect Google Drive.');
  return fetch(vaultApiPath(`/api/google-drive-auth${suffix}`), {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      ...(init?.headers || {}),
    },
  });
}

// ---------------------------------------------------------------------------
// Token cache
// ---------------------------------------------------------------------------

let cachedToken: { token: string; expiresAt: number } | null = null;

export interface DriveAuthError extends Error {
  reconnect?: boolean;
  status?: number;
}

/** Returns a valid Drive access token, minting a fresh one when near expiry. */
export async function getDriveAccessToken(force = false): Promise<string> {
  if (!force && cachedToken && cachedToken.expiresAt - 60_000 > Date.now()) {
    return cachedToken.token;
  }
  const res = await brokerFetch('', { method: 'GET' });
  const data = await res.json();
  if (!res.ok) {
    cachedToken = null;
    const err = new Error(data.error || 'Could not get a Drive access token.') as DriveAuthError;
    err.reconnect = !!data.reconnect;
    err.status = res.status;
    throw err;
  }
  cachedToken = { token: data.accessToken, expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000 };
  return data.accessToken as string;
}

export interface DriveStatus {
  connected: boolean;
  googleEmail: string | null;
  scope: string | null;
}

/** Whether the signed-in user has Drive backup connected (and which account). */
export async function getDriveStatus(): Promise<DriveStatus> {
  const res = await brokerFetch('?status=1', { method: 'GET' });
  if (!res.ok) return { connected: false, googleEmail: null, scope: null };
  return (await res.json()) as DriveStatus;
}

/** Revokes at Google and forgets the stored refresh token. */
export async function disconnectDrive(): Promise<void> {
  cachedToken = null;
  const res = await brokerFetch('', { method: 'DELETE' });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Could not disconnect Google Drive.');
  }
}

async function storeConnection(payload: {
  serverAuthCode?: string;
  codeVerifier?: string;
  redirectUri?: string;
  refreshToken?: string;
  email?: string | null;
}): Promise<void> {
  const res = await brokerFetch('', {
    method: 'POST',
    body: JSON.stringify({ ...payload, scope: DRIVE_APPDATA_SCOPE }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || 'Could not save the Google Drive connection.';
    // 401 means the Supabase session is expired/invalid — tell the user to re-login.
    if (res.status === 401) throw new Error('Session expired — please sign out and sign back in, then reconnect Google Drive.');
    throw new Error(msg);
  }
  if (data.accessToken) {
    cachedToken = { token: data.accessToken, expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000 };
  }
}

// ---------------------------------------------------------------------------
// Connect flows
// ---------------------------------------------------------------------------

/** Native connect: capgo Google Sign-In → Supabase session → broker stores refresh token. */
async function connectDriveNative(): Promise<void> {
  const { idToken, serverAuthCode, accessToken, email } = await nativeGoogleSignIn();

  const supabase = createClient();
  if (idToken && supabase) {
    const { error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken });
    if (error) throw error;
  }

  if (!serverAuthCode) {
    throw new Error('Google did not return offline access. Please reconnect and approve Drive access.');
  }
  await storeConnection({ serverAuthCode, email });

  if (accessToken) {
    cachedToken = { token: accessToken, expiresAt: Date.now() + 50 * 60 * 1000 };
  }
}

/**
 * Web connect step 1: redirect to Google with Drive scope using our own PKCE.
 * Stores the verifier in a cookie (survives the full-page OAuth redirect).
 * Uses state=drive1 so the callback page knows to handle this as a Drive connect.
 */
async function startDriveWebConnect(): Promise<void> {
  const clientId = process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID;
  if (!clientId) throw new Error('NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID is not set.');

  // Capture the Supabase access token NOW (before navigating away) so the
  // callback page can use it directly. session restoration from localStorage
  // is unreliable after a cross-origin redirect.
  const supabase = createClient();
  if (!supabase) throw new Error('Supabase is not configured.');
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not signed in — please sign in to Seamigo first, then connect Google Drive.');
  }

  const verifier = generateVerifier();
  const challenge = await sha256b64url(verifier);
  const redirectUri = `${window.location.origin}/auth/callback`;

  setCookie('drive_cv', verifier, 600);
  setCookie('drive_ru', redirectUri, 600);
  // drive_at is intentionally NOT stored: the Supabase session in localStorage
  // survives the same-origin OAuth redirect, so the callback re-reads it there.

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', clientId);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', `openid email ${DRIVE_APPDATA_SCOPE}`);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');
  url.searchParams.set('state', 'drive1');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');

  window.location.href = url.toString();
}

/**
 * Web connect step 2: called from /auth/callback when state=drive1.
 * Uses the accessToken captured before the OAuth redirect (stored in a cookie)
 * rather than re-reading from Supabase session, which may not survive the
 * cross-origin redirect reliably.
 */
export async function completeDriveWebConnect(
  code: string,
  codeVerifier: string,
  redirectUri: string,
  accessToken: string,
): Promise<void> {
  const body = JSON.stringify({
    serverAuthCode: code,
    codeVerifier,
    redirectUri,
    scope: DRIVE_APPDATA_SCOPE,
  });
  const res = await fetch(vaultApiPath('/api/google-drive-auth'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'content-type': 'application/json' },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) throw new Error('Session expired — please sign out and sign back in, then reconnect Google Drive.');
    throw new Error(data.error || 'Could not save the Google Drive connection.');
  }
  if (data.accessToken) {
    cachedToken = { token: data.accessToken, expiresAt: Date.now() + (data.expiresIn ?? 3600) * 1000 };
  }
}

/**
 * Connect Google Drive cloud backup. On native this completes in-place; on web
 * it redirects to Google and resumes in /auth/callback (completeDriveWebConnect).
 */
export async function connectGoogleDrive(): Promise<void> {
  if (isNativeRuntime()) return connectDriveNative();
  return startDriveWebConnect();
}
